<script>
  import { getContext } from "svelte";
  import { navigate } from "svelte-routing";

  import { Modal } from "svelte-utilities";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";

  import { workingSheetIds, sheetFrames, isFrameBuilt, hasBeenLocated, isScaled } from "./derive.js";
  import { deriveCurrentStep, derivePageStep } from "./steps.js";
  import { plural } from "./labels.js";
  import { setPageFlags } from "./page-flags.js";
  import { rangeBetween } from "./markers.js";
  import { selectAllIds } from "./selection.js";
  import { forceScaleFor } from "./force-scale.js";
  import { createFrameImageTracker } from "./ensure-frame-images.js";
  import StepCard from "./StepCard.svelte";
  import Step1Scale from "./steps/Step1Scale.svelte";
  import Step2Locate from "./steps/Step2Locate.svelte";
  import Step3Analyze from "./steps/Step3Analyze.svelte";
  import Step4Download from "./steps/Step4Download.svelte";
  import { submitShpxRequest } from "#src/api";
  import { fetchShpxAssistData, updateShpxAssistData } from "#src/extensions/shpx-assist-data.js";
  import eb from "#src/extensions/event-bus.js";
  import { alerts } from "#src/stores/alerts.js";
  import { selectedPages } from "#src/stores/ui.js";
  import ContenteditableInput from "#src/lib/sidebar/ContenteditableInput.svelte";

  export let group;
  export let doc;
  export let docid;
  export let indexedLocations;
  export let visiblePages;
  export let visibleIndices;
  export let selectedPageList;
  export let selectedLocationList;
  export let spinners;
  export let collections;
  export let viewer;

  // Edit mode (item overlay open): render the panel dimmed and fully
  // non-interactive rather than reverting to the legacy pages list. Default-off
  // so the normal FrameX render is unchanged.
  export let disabled = false;

  const org = getContext("org");
  const shpxAssistPages = getContext("shpxAssistPages");

  let assistModal;
  let assistMessage = "";
  let termsAgreed = false;

  // Three distinct values, and they can't be collapsed into two: null means "no
  // manual choice" and falls back to the derived current step; 0 means the user
  // explicitly closed the open row, which is a legal state (four bare rows); a
  // step number pins that row open.
  let expandedStep = null;

  $: pages = doc?.data?.pages || {};
  $: workingIds = workingSheetIds(pages, visiblePages);
  $: showEmptyState = workingIds.length === 0 && selectedPageList.length === 0;

  // Membership is derived (isScaled && !notApplicable), so a working sheet
  // can never lack a scale. Step 1 is "done" once anything has joined.
  $: step1Done = workingIds.length > 0;

  // Steps 2, 3 and 4 all work off the same traversal of the working set,
  // flattened for the frame totals and for the list step 3 renders. Derived
  // once here and passed down, so a step body never recomputes it.
  $: allFrames = workingIds.flatMap((id) => sheetFrames(indexedLocations[id]));

  // Frames on FrameX sheets always carry their clipped image, however they were
  // created. Keyed off allFrames so a hand-drawn frame is covered the moment it
  // lands, without FrameX needing to see the canvas gesture that made it.
  const ensureFrameImages = createFrameImageTracker();
  $: void ensureFrameImages(allFrames, group);

  // A sheet counts as located once the locator has run on it OR it has a
  // hand-drawn frame — see hasBeenLocated's doc comment for why those stay
  // separate facts. Step 2 is "done" once every working sheet clears that bar.
  $: step2Done =
    workingIds.length > 0 && workingIds.every((id) => hasBeenLocated(pages[id], indexedLocations[id]));

  // Step 3 is "done" once every frame in the working set is built — i.e. has a
  // width and height (analyzer- or hand-entered).
  $: builtCount = allFrames.filter((loc) => isFrameBuilt($group.items[loc.record_id])).length;
  $: step3Done = allFrames.length > 0 && builtCount === allFrames.length;

  // Where each selected page sits in the ladder. Read only when the page selection
  // changes (the watcher below), never as a live input to what's open — a
  // deliberate page selection is the only thing that moves the stepper.
  $: selectedSteps = selectedPageList.map((p) => {
    const frames = sheetFrames(indexedLocations[p.id]);
    return derivePageStep({
      scaled: isScaled(pages[p.id]),
      frameCount: frames.length,
      builtCount: frames.filter((loc) => isFrameBuilt($group.items[loc.record_id])).length,
    });
  });
  // Step 1 means "unscaled", so a set carrying both 1s and non-1s is the mixed
  // case: forcing either step would be wrong, so nothing is forced and each step's
  // own ScaleWarning says what's blocked. Otherwise the least-advanced page wins —
  // the earliest unfinished work is the useful one to open.
  $: mixedScale = selectedSteps.some((s) => s === 1) && selectedSteps.some((s) => s !== 1);
  $: selectedStep = !selectedSteps.length || mixedScale ? null : Math.min(...selectedSteps);

  // The opening default only: the first rung of the whole working set that isn't
  // satisfied. It decides what's open before the user has selected a page or
  // clicked a row. After that `expandedStep` holds, and the data moving underneath
  // never reopens a different step.
  $: currentStep = deriveCurrentStep({ step1Done, step2Done, step3Done });
  // Which row is open — the only thing that colours a row. A manual click pins
  // expandedStep; until then the current step is open. This lets the panel show
  // step 2 while the data says you're on step 3 — exactly what a second pass of
  // frame-finding wants.
  //
  // Step 4 used to be excluded here, back when a closed row could also be blue:
  // auto-opening Download would have read as "this step has been run". Now that
  // open is the only thing that colours a row, the exception has no reason to
  // exist — and without it there is no state where all four rows sit uniform
  // with nothing to start from.
  //
  // A finished build is the one moment that rule would misfire: the last frame
  // built moves currentStep to 4, which would swap the panel to Download just
  // as step 3 has something to show. Step 3's `built` event pins expandedStep
  // instead, so the jump only happens when the user asks for it.
  $: effectiveExpanded = expandedStep ?? currentStep;

  // Counts describe work in the FrameX set, never the canvas selection, and
  // only steps 2 and 3 have work to describe. A count is omitted entirely until
  // there's a total to report, so rows with nothing to say carry no
  // parenthetical. A row's count does not depend on whether it is open: the
  // rows are the one place these totals are stated, so hiding one on expand
  // would take the number off screen entirely rather than de-duplicate it.
  //
  // Steps 1 and 4 carry none, each for its own reason. Beside "Set Page Scale"
  // a count would read as the scope of the action, and that button acts on the
  // selection. Beside "Export Frames" it would read as frames *exported* — steps 2
  // and 3 report what they produced, so the fourth number in the column is
  // taken the same way — when all it could ever state is what is eligible.
  // That is the same false claim the step-4 tint made, and it is left out for
  // the same reason: nothing here can know what has actually been downloaded.
  $: stepCounts = {
    1: "",
    2: allFrames.length > 0 ? `(${allFrames.length})` : "",
    3: allFrames.length > 0 ? `(${builtCount}/${allFrames.length})` : "",
    4: "",
  };

  // "Pages in FrameX" chips sit below the steps, labeled by page number only.
  // Sheet number/title are deliberately not surfaced here yet — parked for a
  // team discussion (see framex.md).
  //
  // The number must be the same one the Pages tab shows on its row, which is
  // the position in the FULL page order — hidden pages included. `visibleIndices`
  // counts only non-hidden pages, so it drifts from the row number as soon as a
  // page is hidden, and the two lists then point at different pages.
  $: pageNumbers = orderedList(doc?.data?.pages ?? {}).reduce((acc, page, i) => {
    acc[page.id] = i + 1;
    return acc;
  }, {});
  function chipText(pageId) {
    return String(pageNumbers[pageId] ?? "");
  }

  // Chip selection mirrors the frames list: plain click = select only,
  // cmd/ctrl = toggle, shift = range from the last plain/cmd-clicked chip.
  // workingIds is the ordered id list the range is measured across.
  let chipAnchor = null;
  function selectChip(event, pageId) {
    if (event.shiftKey && chipAnchor != null) {
      selectedPages.set(selectAllIds(rangeBetween(workingIds, chipAnchor, pageId)));
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      selectedPages.toggle(pageId);
      chipAnchor = pageId;
      return;
    }
    selectedPages.selectOnly(pageId);
    chipAnchor = pageId;
  }

  // Double-click zooms to the sheet, matching a double-click on a Pages-tab row.
  //
  // Routed through the event bus rather than a component event: Document.svelte
  // subscribes zoomToPage to it (`eb.on("zoom-to-page", …)`) but passes no
  // `on:zoom-to-page` handler to this panel, and LocationThumbnailViewer already
  // reaches it the same way. Nothing leaks into the legacy product — this panel only
  // mounts under `UX_MODE === "shpx" && $leftPanelMode === "framex"`, so the dispatch
  // is gated by construction.
  //
  // The bus takes a bare page id, unlike the `{ page }` detail the Svelte-event
  // path uses.
  function zoomChip(pageId) {
    eb.dispatch("zoom-to-page", pageId);
  }

  // "Remove N" drops the selected member pages from FrameX in bulk. Removal
  // only clears membership (framexExcluded) — it never touches scale or the
  // N/A flag, so the sheet keeps its scale, its corner label and its frames,
  // and can rejoin later. Shows only when the selection includes member pages.
  $: removableCount = selectedPageList.filter((p) => workingIds.includes(p.id)).length;
  function removeSelectedFromFrameX() {
    const ids = selectedPageList.map((p) => p.id).filter((id) => workingIds.includes(id));
    if (ids.length) setPageFlags(group, doc, ids, { framexExcluded: true });
  }

  // A scaled page removed from FrameX (framexExcluded) can rejoin. When such
  // pages are selected, offer a "+ Add page N" tile inline with the chips.
  $: addableIds = selectedPageList
    .map((p) => p.id)
    .filter((id) => isScaled(pages[id]) && pages[id]?.framexExcluded);
  $: addLabel =
    addableIds.length === 1 ? `+ Add page ${chipText(addableIds[0])}` : `+ Add ${addableIds.length} pages`;
  function addSelectedToFrameX() {
    if (addableIds.length) setPageFlags(group, doc, addableIds, { framexExcluded: false });
  }

  $: requestingPages = selectedPageList.length ? selectedPageList : visiblePages;
  $: requestDisabled =
    $org?.shpx_subscription_type !== "team_pro" && requestingPages.length > $org?.shpx_sheet_credits;

  // The footer button counts the live canvas selection; the modal's confirm
  // button counts requestingPages (selection, or every visible page when
  // nothing is selected) since that's what actually gets submitted.
  $: assistButtonLabel = selectedPageList.length
    ? `Send ${selectedPageList.length} page${plural(selectedPageList.length)} to our team`
    : "Send pages to our team";
  $: assistConfirmLabel = `Send ${requestingPages.length} page${plural(requestingPages.length)} to our team`;

  // No step is ever locked: clicking a row opens it, and clicking the row that's
  // already open closes it, leaving the panel as four bare rows. A click and a new
  // page selection are the only two things that move the stepper; it survives
  // everything else, including the data changing underneath.
  function selectStep(n) {
    expandedStep = effectiveExpanded === n ? 0 : n;
  }

  // A user tried to place a frame on an unscaled page (Document.svelte's
  // shpx+framex-gated hook, see force-scale.js, which blocks placement rather
  // than letting it through). Step 1 must be expanded for Step1Scale to be
  // mounted at all -- StepCard only renders its slot while open. This assigns
  // rather than calling selectStep, which would *close* step 1 in the common
  // case where it's already the open row.
  $: if ($forceScaleFor) {
    expandedStep = 1;
  }

  // A new page selection is an explicit "work on this", so it pins the step that
  // page needs. Nothing else writes the pin: selecting frames, deselecting, and the
  // data changing all leave the stepper where it is. A mixed scaled/unscaled
  // selection leaves `selectedStep` null and pins nothing. Compared as a joined id
  // list rather than by reference, since selectedPageList is rebuilt on every store
  // change — the same technique RightPanelTabs uses.
  let prevPageSel = "";
  $: pageSel = selectedPageList.map((p) => p.id).join(",");
  $: if (pageSel !== prevPageSel) {
    if (pageSel && selectedStep) expandedStep = selectedStep;
    prevPageSel = pageSel;
  }

  async function requestAssistance() {
    assistModal.close();

    const pageIds = selectedPageList.length ? selectedPageList.map((p) => p.id) : visiblePages;

    const submitted = await submitShpxRequest({
      organization_id: $org.id,
      job_id: $group.job_id,
      data: {
        name: $group.job.name,
        document_id: docid,
        pages: pageIds,
        details: assistMessage,
      },
    });

    if (!submitted.error) {
      await updateShpxAssistData(pageIds, {
        organization_id: $org.id,
        job_id: $group.job_id,
        document_id: docid,
        status: "submitted",
      });

      const data = await fetchShpxAssistData($group.job_id);
      $shpxAssistPages = data;
      alerts.add({ type: "ok", message: "Your request has been sent!" });

      const numPages = pageIds.length;
      const newPageCount = Math.max(0, $org.shpx_sheet_credits - numPages);
      org.updateProp("shpx_sheet_credits", newPageCount);
    } else {
      alerts.add({ type: "error", message: "There was an issue submitting your request." });
    }

    assistMessage = "";
  }
</script>

<div class="fx-panel" class:fx-panel--disabled={disabled} aria-disabled={disabled}>
  <div class="fx-header">
    <div class="fx-title">FrameX</div>
  </div>

  <div class="fx-body">
    {#if showEmptyState}
      <!-- Empty state only: says what FrameX is for before any sheet has joined.
           Once the steps are on screen, they do the explaining. -->
      <div class="fx-tagline">
        Converts glazing elevations into editable frames. Export directly to WinBidPro (GDS), Glazier Studio,
        or PartnerPak.
      </div>
      <div class="fx-empty">
        <div class="fx-empty-title">Select pages to begin</div>
      </div>
    {:else}
      <div class="fx-steps">
        <StepCard
          number={1}
          title="Set Page Scale"
          count={stepCounts[1]}
          expanded={effectiveExpanded === 1}
          on:toggle={() => selectStep(1)}>
          <Step1Scale
            {group}
            {doc}
            {pages}
            {indexedLocations}
            {selectedPageList}
            on:pick-dimension
            on:click-location-tool />
        </StepCard>

        <StepCard
          number={2}
          title="Find Frames"
          count={stepCounts[2]}
          expanded={effectiveExpanded === 2}
          on:toggle={() => selectStep(2)}>
          <Step2Locate
            {group}
            {doc}
            {pages}
            {indexedLocations}
            {visibleIndices}
            {selectedPageList}
            {collections}
            {viewer}
            {selectedLocationList}
            on:click-location-tool />
        </StepCard>

        <StepCard
          number={3}
          title="Build Frames"
          count={stepCounts[3]}
          expanded={effectiveExpanded === 3}
          on:toggle={() => selectStep(3)}>
          <Step3Analyze
            {group}
            frames={allFrames}
            {builtCount}
            {visibleIndices}
            {pages}
            {selectedPageList}
            {selectedLocationList}
            {spinners}
            on:edit-frame
            on:built={() => (expandedStep = 3)}
            on:continue-to-download={() => selectStep(4)} />
        </StepCard>

        <StepCard
          number={4}
          title="Export Frames"
          count={stepCounts[4]}
          expanded={effectiveExpanded === 4}
          on:toggle={() => selectStep(4)}>
          <Step4Download {group} {workingIds} {selectedLocationList} frames={allFrames} />
        </StepCard>
      </div>

      <!-- Also when nothing is in FrameX but something can be added. The "+ Add
           page" tile below is a removed sheet's only way back from this panel, and
           gating it on there being existing members made removing the last sheet a
           dead end. addableIds follows the selection rather than the working set,
           so this opens as soon as the removed page is selected again. -->
      {#if workingIds.length > 0 || addableIds.length > 0}
        <div class="fx-pages">
          <div class="fx-pages-head">
            <span class="fx-pages-label"
              >Pages in FrameX <span class="fx-pages-count">({workingIds.length})</span></span>
            {#if removableCount > 0}
              <button
                class="fx-pages-remove"
                title="Remove selected pages from FrameX"
                on:click={removeSelectedFromFrameX}>
                Remove {removableCount}
              </button>
            {/if}
          </div>
          <div class="fx-chips">
            {#if addableIds.length > 0}
              <button
                class="fx-chip fx-chip-add"
                title="Add the selected scaled page(s) to FrameX"
                on:click={addSelectedToFrameX}>
                {addLabel}
              </button>
            {/if}
            {#each workingIds as pageId (pageId)}
              <!-- Selecting from the tile mirrors selecting on canvas, which the
                   design calls for and which is far easier to hit than a sheet
                   edge. Shift/cmd/ctrl extend the selection (selectChip), and
                   double-click zooms to the sheet as a Pages-tab row does. -->
              <button
                class="fx-chip"
                class:selected={$selectedPages[pageId]}
                title="Select this page"
                on:click={(e) => selectChip(e, pageId)}
                on:dblclick={() => zoomChip(pageId)}>
                {chipText(pageId)}
              </button>
            {/each}
          </div>
        </div>
      {/if}
    {/if}
  </div>

  <div class="fx-assist">
    <button class="fx-assist-button" on:click={() => assistModal.open()}>
      {assistButtonLabel}
    </button>
    <div class="fx-assist-copy">
      Our pros build and check each frame — dimensions, spacing, and configuration — and flag issues. Sheets
      are delivered ready for your review and download.
    </div>
  </div>
</div>

<Modal
  bind:this={assistModal}
  closeable
  closeOnOutclick
  on:confirm={requestAssistance}
  buttons={[
    { label: "Cancel", type: "cancel" },
    {
      label: assistConfirmLabel,
      type: "confirm",
      style: "primary",
      disabled: requestDisabled || !termsAgreed,
    },
  ]}>
  <div slot="title">Send pages to our team</div>
  <div slot="content" class="space-y-4">
    <div class="text-xs">
      The Shape Exchange team is here to help! We can extract the frames from these pages for you, and prepare
      the export files you require.
    </div>
    <div class="text-xs">
      Typically, results are ready within 24 hours. If we anticipate a longer turnaround, we'll reach out to
      let you know.
    </div>
    {#if !$org?.shpx_assist_enabled}
      <!-- Org-admin config copy, may carry links; not user input. -->
      <!-- eslint-disable-next-line svelte/no-at-html-tags -->
      <p class="text-xs">{@html $org?.shpx_assist_disabled_reason}</p>
    {:else}
      <!-- requestingPages is the canvas selection when there is one and every
           visible sheet otherwise, so one block covers both cases. -->
      <div class="text-xs">
        {#if $org?.shpx_sheet_credits > 0}
          <p>
            <span class="font-bold">Available credits</span>: {$org.shpx_sheet_credits}
          </p>
        {/if}
        <p>
          Requesting assistance for {requestingPages.length}
          {selectedPageList.length ? "selected " : ""}page{plural(requestingPages.length)}.
        </p>
      </div>
      {#if requestingPages.length > $org?.shpx_sheet_credits && $org?.shpx_subscription_type !== "team_pro"}
        <div class="text-xs">
          <p class="text-red-500 font-bold">This request exceeds your credit balance.</p>
          <p>
            <button class="btn-text" on:click={() => navigate("/pricing")}>Upgrade your subscription</button> to
            get additional credits.
          </p>
        </div>
      {/if}
      <div class="text-xs">
        <ContenteditableInput border label="Request Details" bind:value={assistMessage} />
      </div>
      {#if !requestDisabled}
        <div class="flex gap-2 text-xs">
          <input type="checkbox" bind:checked={termsAgreed} />
          <div>
            Agree to <a class="btn-text" href="https://help.shape.exchange/legal-csa" target="_blank"
              >Terms & Conditions</a>
          </div>
        </div>
      {/if}
    {/if}
  </div>
</Modal>

<style lang="scss">
  .fx-panel {
    width: 288px;
    flex: none;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-right: 1px solid var(--fx-border);
    font-family: inherit;
    user-select: none;
    // Legacy text-xs base (matching <SidebarSection>) so inherited controls
    // render at 12px / 1rem rather than the browser default.
    font-size: 12px;
    line-height: 1rem;
  }

  // Edit mode: grey the whole panel out and block every interaction, so the
  // editing overlay keeps FrameX context on screen without letting the user
  // drive it.
  .fx-panel--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  // Step titles/copy and chrome shouldn't highlight on click/drag; inputs
  // inside the panel (and its step bodies) stay selectable/typeable.
  .fx-panel :global(input),
  .fx-panel :global(textarea),
  .fx-panel :global([contenteditable="true"]) {
    user-select: text;
  }

  .fx-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    border-bottom: 1px solid var(--fx-border-light);
  }

  .fx-title {
    flex: 1;
    font-size: 12px;
    font-weight: 800;
    color: var(--fx-text);
  }

  // "Pages in FrameX" — the membership list, below the steps (not above).
  .fx-pages {
    border-top: 1px solid var(--fx-border-light);
    padding-top: 10px;
    margin-top: 2px;
  }

  .fx-pages-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  .fx-pages-label {
    flex: 1;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--fx-text-alt);
  }

  // The count is a parenthetical, so it sits back from the label rather than
  // competing with it — the label's tracking dropped so the digits don't sit
  // loose, and tabular figures (like .fx-chip) so the width doesn't shift as
  // pages join or leave.
  .fx-pages-count {
    color: var(--fx-neutral);
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
  }

  .fx-pages-remove {
    all: unset;
    cursor: pointer;
    font-size: 10px;
    font-weight: 700;
    color: var(--fx-danger);
  }

  .fx-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }

  // Compact position tile. Every chip here is already a FrameX member, so gold
  // is the default state — the same gold the Pages tab puts on a member's number
  // tile. Selection is the canvas's blue border over a light blue fill, matching
  // both the canvas outline and the right-panel rows' selection stroke.
  .fx-chip {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 24px;
    padding: 0 7px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--fx-amber-text);
    background: var(--fx-amber-bg);
    border: 1px solid var(--fx-amber-border);
    cursor: pointer;

    // The border is amber at rest, so hover deepens it to the chip's own text
    // tone rather than restating the border colour.
    &:hover {
      border-color: var(--fx-amber-text);
    }

    // 2px to match the row selection stroke and the canvas outline. The tile
    // is border-box, so the heavier border costs no size. Text returns to the
    // neutral tone — the gold text was chosen against a gold fill.
    &.selected {
      color: var(--fx-text-alt);
      background: var(--fx-accent-tint);
      border: 2px solid var(--fx-select-stroke);
    }
  }

  // "+ Add page N" — a dashed-amber tile that joins the selected scaled page(s)
  // to FrameX, sitting inline before the membership chips.
  .fx-chip-add {
    padding: 0 9px;
    background: #fff;
    color: var(--fx-amber-text);
    border-style: dashed;
    border-color: var(--fx-amber-border);

    &:hover {
      background: var(--fx-amber-bg);
    }
  }

  .fx-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 14px;
  }

  .fx-steps {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  // Empty-state only, and inside .fx-body — so it inherits the body's 14px padding
  // and needs nothing but the gap down to the empty-state card.
  .fx-tagline {
    margin-bottom: 12px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--fx-text-alt);
  }

  // The empty state's one instruction, so it has to read as a prompt rather than a
  // placeholder: a solid border instead of dashed, and a faint fill to lift it off
  // the panel. Deliberately neutral — blue means selected, gold means "in FrameX",
  // amber means a warning, so borrowing any of those here would say something this
  // box doesn't mean. #f7f8fa is the same neutral tint the right-panel rows hover on.
  .fx-empty {
    border: 1px solid var(--fx-border-button);
    border-radius: 6px;
    background: #f7f8fa;
    padding: 18px 16px;
    text-align: center;
  }

  // A step up from the panel's 12px body size, since this is the only thing being
  // asked of the user. The margin-bottom went with the second line it used to
  // separate — without it the single line sat low in the box.
  .fx-empty-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--fx-text);
  }

  .fx-assist {
    flex: none;
    padding: 10px 12px;
    border-top: 1px solid var(--fx-border-light);
  }

  // The service footer is a product, not a help button — a solid teal action
  // with supporting copy, not a bordered "request assist" pill.
  .fx-assist-button {
    display: block;
    width: 100%;
    padding: 8px;
    border-radius: 6px;
    border: 1px solid var(--fx-assist-border);
    background: var(--fx-assist);
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    cursor: pointer;

    &:hover {
      background: var(--fx-assist-hover);
    }
  }

  .fx-assist-copy {
    font-size: 11px;
    line-height: 1.45;
    color: var(--fx-text-secondary);
    margin-top: 7px;
  }
</style>
